class GeolocationService {
  constructor() {
    this.options = {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 5000
    };
  }

  async checkPermission() {
    // Check if we're on HTTPS (required for reliable geolocation)
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    
    if (!navigator.permissions) {
      // Fallback for browsers that don't support Permissions API
      return { 
        state: 'prompt', 
        supported: false, 
        isSecure,
        warning: !isSecure ? 'HTTPS required for reliable location access' : null
      };
    }

    try {
      const permission = await navigator.permissions.query({name: 'geolocation'});
      return { 
        state: permission.state, 
        supported: true,
        isSecure,
        warning: !isSecure ? 'HTTPS required for reliable location access' : null
      };
    } catch (error) {
      // Some browsers might not support geolocation permission query
      return { 
        state: 'prompt', 
        supported: false,
        isSecure,
        warning: !isSecure ? 'HTTPS required for reliable location access' : null
      };
    }
  }

  async requestPermission() {
    // First check if we're on a secure context
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    
    if (!isSecure) {
      throw new Error('Geolocation requires HTTPS or localhost. Please access the application via HTTPS.');
    }

    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    }

    // Try to get current position to trigger permission request
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            granted: true,
            position: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp
            }
          });
        },
        (error) => {
          let message = 'Unknown location error';
          let granted = false;
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information unavailable. Please check your device\'s location settings.';
              break;
            case error.TIMEOUT:
              message = 'Location request timed out. Please try again.';
              break;
          }
          
          resolve({ granted, error: message });
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 5000
        }
      );
    });
  }

  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      // Check if we're on HTTPS
      const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      if (!isSecure) {
        reject(new Error('Geolocation requires HTTPS or localhost. Please access the application via HTTPS.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          });
        },
        (error) => {
          let message = 'Unknown location error';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information unavailable. Please check your device\'s location settings.';
              break;
            case error.TIMEOUT:
              message = 'Location request timed out. Please try again.';
              break;
          }
          reject(new Error(message));
        },
        this.options
      );
    });
  }

  /**
   * Continuously watch the device position. The success callback fires whenever
   * the platform reports a position change (typically ~1Hz while moving with
   * high accuracy), so the caller gets live updates without polling.
   *
   * Uses maximumAge: 0 so a moving device never reuses a stale cached fix.
   *
   * @param {(position: {latitude:number, longitude:number, accuracy:number, timestamp:number}) => void} onUpdate
   * @param {(error: Error) => void} [onError]
   * @returns {number|null} watch id to pass to clearWatch(), or null if unavailable
   */
  watchPosition(onUpdate, onError) {
    if (!navigator.geolocation) {
      if (onError) onError(new Error('Geolocation is not supported by your browser'));
      return null;
    }

    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    if (!isSecure) {
      if (onError) onError(new Error('Geolocation requires HTTPS or localhost. Please access the application via HTTPS.'));
      return null;
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        onUpdate({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        let message = 'Unknown location error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location access denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information unavailable. Please check your device\'s location settings.';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out. Please try again.';
            break;
        }
        if (onError) onError(new Error(message));
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0
      }
    );
  }

  /**
   * Stop a position watch started with watchPosition().
   * @param {number|null} watchId
   */
  clearWatch(watchId) {
    if (watchId != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId);
    }
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in miles
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  isWithinGeofence(caregiverLat, caregiverLng, targetLat, targetLng, radiusMiles = 1) {
    const distance = this.calculateDistance(caregiverLat, caregiverLng, targetLat, targetLng);
    return {
      isWithin: distance <= radiusMiles,
      distance: distance,
      radiusMiles: radiusMiles
    };
  }

  parseGeocodeString(geocodeString) {
    if (!geocodeString || typeof geocodeString !== 'string') {
      return null;
    }

    const parts = geocodeString.split(',');
    if (parts.length !== 2) {
      return null;
    }

    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());

    if (isNaN(lat) || isNaN(lng)) {
      return null;
    }

    return { latitude: lat, longitude: lng };
  }

  formatGeocodeString(latitude, longitude) {
    return `${latitude},${longitude}`;
  }

  async validateGeofenceForShift(caregiverPosition, clientAddresses, radiusMiles = 1) {
    if (!caregiverPosition || !clientAddresses || !Array.isArray(clientAddresses)) {
      return {
        isValid: false,
        reason: 'Invalid parameters provided',
        distances: []
      };
    }

    const results = [];
    let isValidForAnyAddress = false;

    for (const address of clientAddresses) {
      if (!address.geocode) {
        results.push({
          address: this.formatAddress(address),
          isValid: false,
          reason: 'Address not geocoded',
          distance: null
        });
        continue;
      }

      const targetCoords = this.parseGeocodeString(address.geocode);
      if (!targetCoords) {
        results.push({
          address: this.formatAddress(address),
          isValid: false,
          reason: 'Invalid geocode format',
          distance: null
        });
        continue;
      }

      const validation = this.isWithinGeofence(
        caregiverPosition.latitude,
        caregiverPosition.longitude,
        targetCoords.latitude,
        targetCoords.longitude,
        radiusMiles
      );

      results.push({
        address: this.formatAddress(address),
        isValid: validation.isWithin,
        distance: validation.distance,
        reason: validation.isWithin ? 'Within allowed radius' : `Outside ${radiusMiles} mile radius`
      });

      if (validation.isWithin) {
        isValidForAnyAddress = true;
      }
    }

    return {
      isValid: isValidForAnyAddress,
      reason: isValidForAnyAddress 
        ? 'Caregiver is within allowed radius of at least one client address'
        : `Caregiver is not within ${radiusMiles} mile radius of any client address`,
      distances: results,
      caregiverPosition: caregiverPosition,
      radiusMiles: radiusMiles
    };
  }

  formatAddress(address) {
    if (!address) return 'No address available';
    
    const parts = [];
    if (address.address1) parts.push(address.address1);
    if (address.address2) parts.push(address.address2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zip) parts.push(address.zip);
    
    return parts.join(', ') || 'No address available';
  }

  formatDistanceMessage(distance) {
    if (distance === null || distance === undefined) {
      return 'Distance unknown';
    }
    
    if (distance < 0.1) {
      return 'Less than 0.1 miles away';
    }
    
    return `${distance.toFixed(2)} miles away`;
  }
}

export const geolocationService = new GeolocationService();